import React, { useEffect, useRef, useState, useMemo } from "react";
// import { useWindowSize } from "../../hooks/useWindowSize";
import Icon from "../Icon";
import spinner from "../../assets/spinnerloader.svg";
import InfoIcon from "../../assets/icons/info.svg";
import { useWindowSize } from "../../hooks/useWindowSize";

import {
  PlayerWrapper,
  OptimizationBanner,
  TooltipWrapper,
  TooltipText,
  // DeviceWarning
} from "./styles";

/**
 * Props del componente BasicVideoPlayer
 */
type VideoPlayerProps = {
  videoUrl: string;                    // URL del video a reproducir
  isCompressed?: boolean;              // Si el video está comprimido
  blockDownload?: boolean;             // Bloquear descarga del video
  isOwner?: boolean;                   // Si el usuario es propietario del video
  canBeCompressed?: boolean;           // Si el video puede ser comprimido
  optimizationRate?: number;           // Porcentaje de optimización del video
  mode?: 'edit' | 'preview';          // Modo de visualización (edición o preview)
  editable?: boolean;                  // Compatibilidad con editor de texto enriquecido
  onLoadedMetadata?: (videoEl: HTMLVideoElement) => void; // Callback cuando se cargan los metadatos
  setMaxSizes?: boolean;               // Controlar ancho máximo basado en orientación
  videoWidth?: number;                 // Ancho del video para aspect ratio dinámico
  videoHeight?: number;                // Alto del video para aspect ratio dinámico
};

/**
 * Componente BasicVideoPlayer - Reproductor de video básico con funcionalidades avanzadas
 * 
 * Características principales:
 * - Detección automática de orientación (vertical/horizontal)
 * - Control de dimensiones máximas responsivas
 * - Bloqueo de descarga para protección de contenido
 * - Indicadores de optimización y compresión
 * - Compatibilidad con editor de texto enriquecido
 * 
 * @param videoUrl - URL del video a reproducir
 * @param blockDownload - Bloquear descarga del video (default: true)
 * @param isOwner - Si el usuario es propietario del video
 * @param optimizationRate - Porcentaje de optimización del video
 * @param canBeCompressed - Si el video puede ser comprimido
 * @param isCompressed - Si el video está comprimido
 * @param mode - Modo de visualización (edit/preview)
 * @param editable - Compatibilidad con editor de texto enriquecido
 * @param onLoadedMetadata - Callback cuando se cargan los metadatos
 * @param setMaxSizes - Controlar ancho máximo basado en orientación
 */
const CustomVideoPlayer: React.FC<VideoPlayerProps> = ({
  videoUrl,
  blockDownload = true,
  isOwner,
  optimizationRate,
  canBeCompressed,
  isCompressed,
  mode = 'preview',
  editable = false,
  onLoadedMetadata,
  setMaxSizes = false,
  videoWidth,
  videoHeight,
}) => {
  // Referencias y estados del componente
  const videoRef = useRef<HTMLVideoElement>(null);
  const { isMobile } = useWindowSize();
  const [isVertical, setIsVertical] = useState(false);
  const [detectedVideoWidth, setDetectedVideoWidth] = useState<number | null>(null);
  const [detectedVideoHeight, setDetectedVideoHeight] = useState<number | null>(null);
  // const [isUnsupportedDevice, setIsUnsupportedDevice] = useState(false);

  /**
   * Calcula el aspect ratio dinámicamente basado en las dimensiones del video
   * Prioridad: dimensiones detectadas del video > props > aspect ratios estándar
   * Optimizado con useMemo para evitar recálculos innecesarios
   */
  const dynamicAspectRatio = useMemo((): string => {
    // Prioridad 1: dimensiones detectadas del video
    if (detectedVideoWidth && detectedVideoHeight) {
      // Aspect ratio siempre es ancho/alto (width/height)
      return `${detectedVideoWidth} / ${detectedVideoHeight}`;
    }

    // Prioridad 2: dimensiones proporcionadas como props
    if (videoWidth && videoHeight) {
      // Aspect ratio siempre es ancho/alto (width/height)
      return `${videoWidth} / ${videoHeight}`;
    }

    // Prioridad 3: fallback a aspect ratios estándar
    return isVertical ? '9 / 16' : '16 / 9';
  }, [detectedVideoWidth, detectedVideoHeight, videoWidth, videoHeight, isVertical]);

  /**
   * Calcula las dimensiones máximas basadas en la orientación del video
   * - Videos verticales: máximo 320px de ancho
   * - Videos horizontales: máximo 640px de ancho
   * - Si setMaxSizes es false: usa 100% de ancho
   * Optimizado con useMemo para evitar recálculos innecesarios
   */
  const maxDimensions = useMemo(() => {
    if (!setMaxSizes) {
      return { maxWidth: '100%', height: 'auto' };
    }

    if (isVertical) {
      return { maxWidth: '320px', height: 'auto' };
    } else {
      return { maxWidth: '640px', height: 'auto' };
    }
  }, [setMaxSizes, isVertical]);

  /**
   * Efecto para detectar la orientación y dimensiones del video
   * - Detecta automáticamente las dimensiones desde el elemento video
   * - Actualiza el estado isVertical y las dimensiones detectadas
   * - Ejecuta el callback onLoadedMetadata si está disponible
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleMetadata = () => {
      const { videoWidth: vWidth, videoHeight: vHeight } = video;

      // Verificar si las dimensiones son válidas
      if (vWidth && vHeight) {
        // Detectar orientación y dimensiones del video
        const isVideoVertical = vHeight > vWidth;
        setIsVertical(isVideoVertical);

        // Guardar dimensiones detectadas del video
        setDetectedVideoWidth(vWidth);
        setDetectedVideoHeight(vHeight);
      }

      // Call onLoadedMetadata callback if provided (for rich text editor)
      if (onLoadedMetadata) {
        onLoadedMetadata(video);
      }
    };

    // Agregar múltiples eventos para asegurar que se capture
    video.addEventListener("loadedmetadata", handleMetadata);
    video.addEventListener("loadeddata", handleMetadata);
    video.addEventListener("canplay", handleMetadata);

    // También verificar si ya está cargado
    if (video.readyState >= 1) {
      handleMetadata();
    }

    return () => {
      video.removeEventListener("loadedmetadata", handleMetadata);
      video.removeEventListener("loadeddata", handleMetadata);
      video.removeEventListener("canplay", handleMetadata);
    };
  }, [videoUrl, onLoadedMetadata]);

  /**
   * Efecto para activar el volumen automáticamente al reproducir el video
   * - Desactiva el mute cuando el video comienza a reproducirse
   * - Mejora la experiencia del usuario al no requerir activar volumen manualmente
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      if (video.muted) {
        video.muted = false;
      }
      video.removeEventListener("play", handlePlay);
    };

    video.addEventListener("play", handlePlay);
    return () => {
      video.removeEventListener("play", handlePlay);
    };
  }, []);

  return (
    <PlayerWrapper
      isVertical={isVertical}
      isMobile={isMobile}
      aspectRatio={dynamicAspectRatio}
      contentEditable={editable ? 'true' : 'false'}
      style={setMaxSizes ? {
        maxWidth: maxDimensions.maxWidth,
        height: maxDimensions.height,
      } : {}}
    >
      {/* Banner de optimización - Solo visible en modo preview para propietarios */}
      {mode === 'preview' && isOwner && !!canBeCompressed && (
        <OptimizationBanner>
          {!isCompressed ? (
            <>
              💡 Klouser está optimizando este video{" "}
              <Icon icon={spinner} size="12px" color="white" />
            </>
          ) : (
            `🚀 El peso del video fue optimizado${optimizationRate ? ` un ${optimizationRate}%` : ""}`
          )}
          <TooltipWrapper>
            <Icon icon={InfoIcon} size="14px" color="white" style={{ cursor: "pointer" }} />
            <TooltipText>
              {!isCompressed
                ? "Estamos comprimiendo este video para reducir su peso sin perder calidad. Mientras tanto se puede consumir el video original. Solo tú ves este mensaje."
                : "Klouser optimizó este video para mejorar la experiencia de tus suscriptors. Solo tú ves este mensaje."}
            </TooltipText>
          </TooltipWrapper>
        </OptimizationBanner>
      )}

      {/* Elemento de video con controles y protección de descarga */}
      <video
        ref={videoRef}
        playsInline
        controls
        muted
        crossOrigin="anonymous"
        preload="metadata"
        onContextMenu={(e) => e.preventDefault()}
        controlsList={blockDownload && !isOwner ? "nodownload" : undefined}
        style={mode === 'edit' ? {
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          background: '#000',
        } : {}}
      >
        <source src={videoUrl} type="video/mp4" />
        ⚠️ Tu dispositivo no soporta bien este video.
        <a href={videoUrl} download>Descargar video</a>
      </video>
    </PlayerWrapper>
  );
};

export default React.memo(CustomVideoPlayer);
